import * as R from 'ramda';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { ResponseCurves } from '@blisspointmedia/bpm-types/dist/GeoMMM';
import {
  capitalizeWords,
  currencyFormatter,
  getTotalsByDate,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';

export const OUTCOME_TYPE_STRING = {
  kpiVolume: 'KPI Volume',
  revenue: 'Revenue'
};

export const OUTCOME_FORMATTER_TYPE = {
  kpiVolume: numberFormatter,
  revenue: currencyFormatter
};

/*
 * INVESTMENT PERFORMANCE
 */

export const formattedSnapShotData = ({
  data,
  groupBy,
  conversionPrettyName,
  type,
  outcomeVariable
}: {
  data: any[];
  groupBy: string;
  conversionPrettyName: string;
  type: string;
  outcomeVariable: string;
}): { barData; titleData } => {
  let groupedData;
  switch (groupBy) {
    case 'platform': {
      // Group the data by channel and then by platform
      const chanPlatGrouped = R.pipe(
        // @ts-ignore
        R.groupBy(R.prop('channel')),
        // @ts-ignore
        R.map(R.groupBy(R.prop('platform')))
        // @ts-ignore
      )(data);

      // format grouped data to have key as [channel, platform]: data instead of channel: {platform: data}
      const formattedChanPlat = {} as Record<string, any>;
      Object.keys(chanPlatGrouped as Record<string, any>).forEach((key: string) => {
        Object.keys((chanPlatGrouped as Record<string, any>)[key]).forEach((platform: string) => {
          formattedChanPlat[`${key}${platform ? `, ${platform}` : ''}`] = [
            (chanPlatGrouped as Record<string, any>)[key][platform]
          ];
        });
      });

      Object.keys(formattedChanPlat).forEach(key => {
        [formattedChanPlat[key]] = formattedChanPlat[key];
      });

      groupedData = formattedChanPlat;
      break;
    }
    case 'name': {
      // Group by the full breakout, keyed by the non-null "[channel, platform, tactic, brand]" parts
      const grouped: Record<string, any[]> = {};
      data.forEach(row => {
        const parts = [row.channel];
        if (row.platform) {
          parts.push(row.platform);
        }
        if (row.tactic) {
          parts.push(row.tactic);
        }
        if (row.brand_nonbrand) {
          parts.push(row.brand_nonbrand);
        }
        const key = parts.join(', ');
        (grouped[key] = grouped[key] || []).push(row);
      });

      groupedData = grouped;
      break;
    }
    default:
      groupedData = R.pipe(
        // @ts-ignore
        R.groupBy(R.prop(groupBy))
        // @ts-ignore
      )(data);
  }

  // Sum of incrementalOutcome values by grouping
  const sumOfOutcome: Record<string, { name: string; value: number }> = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    // Sum of incrementalOutcome values
    const incrementalOutcomeSum = R.pipe(
      // @ts-ignore
      R.pluck('incrementalOutcome'),
      R.sum
      // @ts-ignore
    )(groupedData[item]);

    sumOfOutcome[item] = {
      name: capitalizeWords(item),
      value: incrementalOutcomeSum
    };
  });

  // Sum of incrementalOutcome lower bounds by grouping
  const sumOfOutcomeLower: Record<string, { name: string; value: number }> = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    const incrementalOutcomeLowerSum = R.pipe(
      // @ts-ignore
      R.pluck('incrementalOutcomeLower'),
      R.sum
      // @ts-ignore
    )(groupedData[item]);
    sumOfOutcomeLower[item] = {
      name: capitalizeWords(item),
      value: incrementalOutcomeLowerSum
    };
  });

  // Sum of incrementalOutcome upper bounds by grouping
  const sumOfOutcomeUpper: Record<string, { name: string; value: number }> = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    const incrementalOutcomeUpperSum = R.pipe(
      // @ts-ignore
      R.pluck('incrementalOutcomeUpper'),
      R.sum
      // @ts-ignore
    )(groupedData[item]);
    sumOfOutcomeUpper[item] = {
      name: capitalizeWords(item),
      value: incrementalOutcomeUpperSum
    };
  });

  // Sum of spend values by grouping
  const sumOfSpend: Record<string, { name: string; value: number }> = {};
  Object.keys(groupedData as any[]).forEach((item: any) => {
    // Sum of incrementalOutcome values
    const spendSum = R.pipe(
      // @ts-ignore
      R.pluck('spend'),
      R.sum
      // @ts-ignore
    )(groupedData[item]);

    sumOfSpend[item] = {
      name: capitalizeWords(item),
      value: spendSum
    };
  });

  // @ts-ignore
  const sumOfOutcomeAll = R.pipe(R.pluck('value'), R.sum)(Object.values(sumOfOutcome));
  // @ts-ignore
  const sumOfSpendAll = R.pipe(R.pluck('value'), R.sum)(Object.values(sumOfSpend));
  // TODO: Update later to account for covariates
  // const sumOfOutcomeAllSansCovariates =
  //   sumOfOutcomeAll - (sumOfOutcome.find(sum => sum.name === "Baseline")?.value ?? 0);
  const overallCPA = sumOfSpendAll / sumOfOutcomeAll;
  const overallROAS = sumOfOutcomeAll / sumOfSpendAll;

  const calcCpaRoasSpend = Object.keys(groupedData as any[]).map((key: any) => {
    const spendSum = sumOfSpend[key].value;

    // Sum of incremental outcome values
    const incrementalOutcomeSum = sumOfOutcome[key].value;
    const incrementalOutcomeSumLower = sumOfOutcomeLower[key].value;
    const incrementalOutcomeSumUpper = sumOfOutcomeUpper[key].value;

    const { spendOverOutcome, spendOverOutcomeLower, spendOverOutcomeUpper } = (() => {
      const calcSpendOverOutcome = spendSum / incrementalOutcomeSum;
      const calcSpendOverOutcomeLower = spendSum / incrementalOutcomeSumLower;
      const calcSpendOverOutcomeUpper = spendSum / incrementalOutcomeSumUpper;

      return {
        spendOverOutcome:
          calcSpendOverOutcome === Infinity || Number.isNaN(calcSpendOverOutcome)
            ? null
            : calcSpendOverOutcome,
        spendOverOutcomeLower:
          calcSpendOverOutcomeLower === Infinity || Number.isNaN(calcSpendOverOutcomeLower)
            ? null
            : calcSpendOverOutcomeLower,
        spendOverOutcomeUpper:
          calcSpendOverOutcomeUpper === Infinity || Number.isNaN(calcSpendOverOutcomeUpper)
            ? null
            : calcSpendOverOutcomeUpper
      };
    })();

    const { outcomeOverSpend, outcomeOverSpendLower, outcomeOverSpendUpper } = (() => {
      const calcOutcomeOverSpend = incrementalOutcomeSum / spendSum;
      const calcOutcomeOverSpendLower = incrementalOutcomeSumLower / spendSum;
      const calcOutcomeOverSpendUpper = incrementalOutcomeSumUpper / spendSum;

      return {
        outcomeOverSpend:
          calcOutcomeOverSpend === Infinity || Number.isNaN(calcOutcomeOverSpend)
            ? null
            : calcOutcomeOverSpend,
        outcomeOverSpendLower:
          calcOutcomeOverSpendLower === Infinity || Number.isNaN(calcOutcomeOverSpendLower)
            ? null
            : calcOutcomeOverSpendLower,
        outcomeOverSpendUpper:
          calcOutcomeOverSpendUpper === Infinity || Number.isNaN(calcOutcomeOverSpendUpper)
            ? null
            : calcOutcomeOverSpendUpper
      };
    })();

    const spendAndEffectShare = {
      name: capitalizeWords(key),
      value: (spendSum / sumOfSpendAll) * 100,
      value2: (incrementalOutcomeSum / sumOfOutcomeAll) * 100,
      label: `Spend ${currencyFormatter.format(spendSum)}`,
      label2: {
        top: conversionPrettyName,
        bottom:
          outcomeVariable === 'revenue'
            ? currencyFormatter.format(incrementalOutcomeSum)
            : numberFormatter.format(incrementalOutcomeSum)
      },
      spendOverOutcome,
      spend_volume: spendSum,
      effect_volume: incrementalOutcomeSum
    };

    if (type === 'spendAndEffectShare') {
      return spendAndEffectShare;
    }

    const val = type === 'roas' ? outcomeOverSpend : spendOverOutcome;
    const valLower = type === 'roas' ? outcomeOverSpendLower : spendOverOutcomeUpper;
    const valUpper = type === 'roas' ? outcomeOverSpendUpper : spendOverOutcomeLower;

    return {
      name: capitalizeWords(key),
      value: val,
      valueLower: valLower,
      valueUpper: valUpper,
      errorX:
        key !== 'baseline' && val != null && valLower != null && valUpper != null
          ? [val - valLower, valUpper - val]
          : undefined,
      label:
        type === 'roas'
          ? `${((outcomeOverSpend ?? 0) / overallROAS).toFixed(2)} x overall`
          : `${((spendOverOutcome ?? 0) / overallCPA).toFixed(2)} x overall`,
      labelRawNumber:
        type === 'roas'
          ? (outcomeOverSpend ?? 0) / overallROAS
          : (spendOverOutcome ?? 0) / overallCPA
    };
  });

  const responseBarData = Object.keys(groupedData as any[]).map((key: any) => {
    const val = sumOfOutcome[key].value;
    const lower = sumOfOutcomeLower[key].value;
    const upper = sumOfOutcomeUpper[key].value;

    const responseBarValue = (val / sumOfOutcomeAll) * 100;
    const lowerBarValue = (lower / sumOfOutcomeAll) * 100;
    const upperBarValue = (upper / sumOfOutcomeAll) * 100;

    return {
      name: sumOfOutcome[key].name,
      value: responseBarValue,
      valueLower: lowerBarValue,
      valueUpper: upperBarValue,
      errorX:
        key !== 'baseline' &&
        responseBarValue != null &&
        lowerBarValue != null &&
        upperBarValue != null
          ? [responseBarValue - lowerBarValue, upperBarValue - responseBarValue]
          : undefined,
      label:
        outcomeVariable === 'revenue' ? currencyFormatter.format(val) : numberFormatter.format(val),
      labelRawNumber: val
    };
  });

  switch (type) {
    case 'revenue':
      return { barData: responseBarData, titleData: currencyFormatter.format(sumOfOutcomeAll) };
    case 'kpiVolume':
      return { barData: responseBarData, titleData: numberFormatter.format(sumOfOutcomeAll) };
    case 'costPerAcquisition':
      return {
        barData: calcCpaRoasSpend,
        titleData: currencyFormatter.format(overallCPA)
      };
    case 'roas':
      return { barData: calcCpaRoasSpend, titleData: overallROAS.toFixed(2) };
    default:
      return {
        barData: calcCpaRoasSpend,
        titleData: null
      };
  }
};

const getNewD = ({
  totalsByDate,
  relative
}: {
  totalsByDate: Record<string, Record<string, number>>;
  relative: boolean;
}) => {
  const newD = [] as any[];
  Object.keys(totalsByDate).forEach((date, index) => {
    let dateValues = totalsByDate[date];
    // if relative view for chart; divide each value by the total sum of the date
    if (relative) {
      const keys = R.keys(totalsByDate[date]);
      let totalSum = 0;
      // Exclude the lower, upper, and band values form total sum
      keys.forEach(key => {
        if (!key.includes('_$')) {
          totalSum += totalsByDate[date][key];
        }
      });
      // const totalSum = R.sum(R.values(totalsByDate[key]));
      const relativeValues: Record<string, number> = {};
      R.keys(totalsByDate[date]).forEach(item => {
        relativeValues[item] = R.divide(totalsByDate[date][item], totalSum) * 100;
      });
      dateValues = relativeValues;
    }
    newD[index] = {};
    newD[index].date = date;

    newD[index] = { ...newD[index], ...dateValues };
  });
  return newD;
};

// Aggregates one series' rows for a single date into a chart row, carrying confidence-interval
// bands (_$lower/_$upper/_$band). With data2Key the value is a ratio (ROAS/CPA) with CI from the
// matching bound; otherwise a summed total. Spend under 10 is treated as 0 and carries no bounds.
const buildDateEntry = ({
  seriesKey,
  rows,
  date,
  data1Key,
  data2Key
}: {
  seriesKey: string;
  rows: any[];
  date: string;
  data1Key: string;
  data2Key?: string;
}): Record<string, any> => {
  const [dKeyTotal, dKeyLower, dKeyUpper] = rows.reduce(
    ([acc, lower, upper], obj) => {
      // treat spend < 10 as 0
      let estSum;
      let lowerSum;
      let upperSum;

      if (data1Key === 'spend' && obj[data1Key] < 10) {
        estSum = acc;
      } else {
        estSum = acc + obj[data1Key];
      }
      // Spend doesn't have lower and upper bounds
      if (data1Key !== 'spend') {
        lowerSum = lower + obj[`${data1Key}Lower`];
        upperSum = upper + obj[`${data1Key}Upper`];
      } else {
        lowerSum = estSum;
        upperSum = estSum;
      }

      return [estSum, lowerSum, upperSum];
    },
    [0, 0, 0]
  );

  if (data2Key) {
    const [d2KeyTotal, d2KeyLower, d2KeyUpper] = rows.reduce(
      ([acc, lower, upper], obj) => {
        let estSum;
        let lowerSum;
        let upperSum;

        if (data2Key === 'spend' && obj[data2Key] < 10) {
          estSum = acc;
        } else {
          estSum = acc + obj[data2Key];
        }
        // Spend doesn't have lower and upper bounds
        if (data2Key !== 'spend') {
          lowerSum = lower + obj[`${data2Key}Lower`];
          upperSum = upper + obj[`${data2Key}Upper`];
        } else {
          lowerSum = estSum;
          upperSum = estSum;
        }

        return [estSum, lowerSum, upperSum];
      },
      [0, 0, 0]
    );

    let calcVal;
    let calcValLower;
    let calcValUpper;
    // calculating ROAS or CPA
    if (d2KeyTotal === 0) {
      calcVal = null;
      calcValLower = null;
      calcValUpper = null;
    } else {
      calcVal = dKeyTotal / d2KeyTotal;

      if (data1Key !== 'spend') {
        // ROAS calculation: outcome/spend
        calcValLower = dKeyLower / d2KeyTotal;
        calcValUpper = dKeyUpper / d2KeyTotal;
      } else {
        // CPA calculation: spend/outcome
        calcValLower = d2KeyUpper === 0 ? null : dKeyTotal / d2KeyUpper;
        calcValUpper = d2KeyLower === 0 ? null : dKeyTotal / d2KeyLower;
      }
    }

    return {
      date,
      [seriesKey]: calcVal === Infinity || Number.isNaN(calcVal) ? null : calcVal,
      [`${seriesKey}_$lower`]:
        calcValLower === Infinity || Number.isNaN(calcValLower) ? null : calcValLower,
      [`${seriesKey}_$upper`]:
        calcValUpper === Infinity || Number.isNaN(calcValUpper) ? null : calcValUpper,
      [`${seriesKey}_$band`]: Number.isNaN(calcValUpper - calcValLower)
        ? null
        : calcValUpper - calcValLower
    };
  }

  return {
    date,
    [seriesKey]: dKeyTotal,
    [`${seriesKey}_$lower`]: dKeyLower,
    [`${seriesKey}_$upper`]: dKeyUpper,
    [`${seriesKey}_$band`]: Number.isNaN(dKeyUpper - dKeyLower) ? null : dKeyUpper - dKeyLower
  };
};

// Change cptb data into a format that can be used by the chart
// {data: string, name of type: spend}
// also returns areas list from name attribute
// when y axis needs to be calculated from two data points, data1Key/data2Key is used
export const formattedAreaData = ({
  data,
  data1Key,
  groupBy,
  dateGrouping,
  data2Key,
  relative = false
}: {
  data: any[];
  data1Key: string;
  groupBy: string;
  dateGrouping: string;
  data2Key?: string;
  relative?: boolean;
}): { newD: any[]; areas: any; groupedAreas: any } => {
  let channels: string[] = [];
  let groupedData: any[] = [];
  let grouping: Record<string, any> | undefined;

  switch (groupBy) {
    case 'channel': {
      const groupByChannel = R.groupBy(item => R.prop(groupBy, item), data);
      channels = Object.keys(groupByChannel);
      groupedData = Object.keys(groupByChannel).map(key =>
        R.groupBy(item => R.prop('date', item), groupByChannel[key])
      );
      break;
    }
    case 'platform': {
      // group by channel and then platform
      const d = R.groupBy(item => R.prop('channel', item), data);
      const chanPlat = {} as any;
      Object.keys(d).forEach(key => {
        chanPlat[key] = R.groupBy(item => R.prop('platform', item), d[key]);
      });

      interface TransformedChanPlatData {
        data: any[];
        grouping: {
          channel: string;
          platform: string | null;
        };
      }
      const transformChanPlat = (
        originalData: Record<string, Record<string, Record<string, Record<string, any[]>>>>
      ): Record<string, TransformedChanPlatData> => {
        const transformedData: Record<string, TransformedChanPlatData> = {};

        Object.keys(originalData).forEach(channel => {
          Object.keys(originalData[channel]).forEach(platform => {
            const key = `${channel},${platform}`;
            const platformData = originalData[channel][platform];
            const getPlatformLabel = (): string => {
              if (platform !== 'null') {
                return capitalizeWords(platform);
              }
              return Object.values(originalData[channel]).length === 1
                ? 'Total'
                : `All ${capitalizeWords(channel)}`;
            };
            transformedData[key] = {
              data: [platformData],
              grouping: {
                channel: capitalizeWords(channel),
                platform: getPlatformLabel()
              }
            };
          });
        });

        return transformedData;
      };

      const transformedChanPlat = transformChanPlat(chanPlat);

      // format grouped data to have key as [channel, platform]: data instead of channel: {platform: data}
      const formattedChanPlat = {} as any;
      Object.keys(chanPlat).forEach(key => {
        Object.keys(chanPlat[key]).forEach(platform => {
          formattedChanPlat[`${key},${platform}`] = chanPlat[key][platform];
        });
      });

      channels = Object.keys(formattedChanPlat);
      groupedData = Object.keys(formattedChanPlat).map(key =>
        // @ts-ignore
        R.groupBy(item => R.prop('date', item), formattedChanPlat[key])
      );
      grouping = Object.fromEntries(
        Object.keys(transformedChanPlat).map(key => [
          key.replace(' ', ''),
          transformedChanPlat[key].grouping
        ])
      );

      break;
    }
    case 'name': {
      // group by the full breakout (channel → platform → tactic → brand)
      const grouped: Record<string, any> = {};
      data.forEach(item => {
        const { channel } = item;
        const platform = item.platform ?? 'null';
        const tactic = item.tactic ?? 'null';
        const brand = item.brand_nonbrand ?? 'null';
        grouped[channel] = grouped[channel] || {};
        grouped[channel][platform] = grouped[channel][platform] || {};
        grouped[channel][platform][tactic] = grouped[channel][platform][tactic] || {};
        grouped[channel][platform][tactic][brand] = grouped[channel][platform][tactic][brand] || [];
        grouped[channel][platform][tactic][brand].push(item);
      });

      const transformed: Record<string, { data: any[]; grouping: any }> = {};
      Object.keys(grouped).forEach(channel => {
        Object.keys(grouped[channel]).forEach(platform => {
          Object.keys(grouped[channel][platform]).forEach(tactic => {
            Object.keys(grouped[channel][platform][tactic]).forEach(brand => {
              const key = `${channel},${platform},${tactic},${brand}`;
              const isPlatformless = platform === 'null' && tactic === 'null' && brand === 'null';
              transformed[key] = {
                data: grouped[channel][platform][tactic][brand],
                grouping: isPlatformless
                  ? {
                      channel: capitalizeWords(channel),
                      platform: `All ${capitalizeWords(channel)}`,
                      tactic: null,
                      brand_nonbrand: null
                    }
                  : {
                      channel: capitalizeWords(channel),
                      platform: platform === 'null' ? null : capitalizeWords(platform),
                      tactic: tactic === 'null' ? null : capitalizeWords(tactic),
                      brand_nonbrand: brand === 'null' ? null : capitalizeWords(brand)
                    }
              };
            });
          });
        });
      });

      channels = Object.keys(transformed);
      groupedData = channels.map(key =>
        R.groupBy(item => R.prop('date', item), transformed[key].data)
      );
      grouping = Object.fromEntries(
        Object.keys(transformed).map(key => [key, transformed[key].grouping])
      );
      break;
    }
    default:
      break;
  }

  // Aggregate each series' rows per date (with CI bands via buildDateEntry), then roll up by date.
  const seriesData: Record<string, any[]> = {};
  groupedData.forEach((dateMap, index) => {
    const seriesKey = channels[index];
    seriesData[seriesKey] = Object.keys(dateMap).map(date =>
      buildDateEntry({ seriesKey, rows: dateMap[date], date, data1Key, data2Key })
    );
  });

  const groupedByDate = R.groupBy(item => R.prop('date', item), Object.values(seriesData).flat());
  const totalsByDate = getTotalsByDate(dateGrouping, groupedByDate, {});
  const newD = getNewD({ totalsByDate, relative });

  const keys = Object.keys(newD[0]).filter(
    key =>
      key !== 'date' &&
      !key.includes('_$lower') &&
      !key.includes('_$upper') &&
      !key.includes('_$band')
  );

  let areas: {
    name: string;
    dataKey: string;
    grouping: { channel: string; platform: string; tactic: string; brand: string };
  }[] = [];
  keys.forEach((key, index) => {
    const normalizedKey = groupBy === 'platform' ? key.replace(' ', '') : key;
    const groupingInfo = grouping ? grouping[normalizedKey] : null;
    // For the full breakout, build a readable name from the non-null grouping parts (the raw
    // dataKey contains "null" placeholders for absent dimensions).
    const areaName =
      groupBy === 'name' && groupingInfo
        ? [
            groupingInfo.channel,
            groupingInfo.platform,
            groupingInfo.tactic,
            groupingInfo.brand_nonbrand
          ]
            .filter(Boolean)
            .join(', ')
        : capitalizeWords(normalizedKey);

    areas[index] = {
      dataKey: normalizedKey,
      grouping: groupingInfo,
      name: areaName
    };
  });

  let groupedAreas = [] as any[];
  if (Array.isArray(areas) && areas.length > 0 && areas[0].grouping) {
    groupedAreas = areas.reduce((acc: { channel: string; areas: any[] }[], obj) => {
      const { channel } = obj.grouping || { channel: 'unknown' }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  areas = R.sortBy(R.compose(R.toLower, R.prop('name')))(areas);
  groupedAreas = R.sortBy(R.compose(R.toLower, R.prop('channel')))(groupedAreas);

  return { newD, areas, groupedAreas };
};

// Summary Metrics values are plain numbers in Meridian runs and display strings ("$3.21",
// "20.12%", "$50,000.00") in workbook runs.
export const parseSummaryNumber = (value: unknown): number =>
  Number(String(value ?? '').replace(/[$,%]/g, ''));

export const percentageToSplitFraction = (percentage: number): string => {
  const holdout = Math.round(percentage);
  const nonHoldout = 100 - holdout;
  return `${nonHoldout}/${holdout}`;
};

// A kpiVolume client's sheet either has Econ's new "Next Dollar Marginal CPA" / bound columns
// for every channel or none at all - a single presence check across the dataset is sufficient
// to decide whether to use the new CPA framing or fall back to the pre-existing effectiveness
// framing for clients Econ hasn't re-exported yet.
export const hasNextDollarMarginalCpa = (data: any[]): boolean =>
  data.some(
    item =>
      item['Next Dollar Marginal CPA'] !== null && item['Next Dollar Marginal CPA'] !== undefined
  );

export const formatSnapshotData = (
  data: any[],
  outcomeVariable: 'revenue' | 'kpiVolume'
): any[] => {
  const useNewCpaFraming = outcomeVariable === 'kpiVolume' && hasNextDollarMarginalCpa(data);
  return data.map((item: any) => ({
    name: capitalizeWords(item.channel).replace(/,/g, ' –'),
    value: useNewCpaFraming
      ? item['Next Dollar Marginal CPA']
      : item['Next Best Dollar Effectiveness']
  }));
};

/*
 * MODEL INSIGHTS
 */

export const saturationDataFormatter = (
  data: ResponseCurves[],
  channelPlatformCounts: Record<string, Set<string>>
): { newD; meanValues; groupedLines } => {
  const d = R.groupBy(item => R.prop('name', item), data);

  const meanValues = {};
  for (const [key, value] of Object.entries(d)) {
    const meanRow = value.find(row => row.spendMultiplier === 1);
    meanValues[key] = meanRow
      ? { mean: meanRow.spend, dataPoint: meanRow.spend }
      : { mean: null, dataPoint: null };
  }

  const transformedData: {
    name: string;
    data: any[];
    grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  }[] = R.pipe(
    R.toPairs,
    R.map(([name, groupData]: [string, any[]]) => {
      const channel = capitalizeWords(groupData[0].channel);
      let platform = capitalizeWords(groupData[0].platform);
      const tactic = capitalizeWords(groupData[0].tactic);
      const brand_nonbrand = capitalizeWords(groupData[0].brand_nonbrand);
      if (!platform && !brand_nonbrand && !tactic) {
        // Check if this channel has multiple platforms using channelPlatformCounts
        const channelName = groupData[0].channel;
        const platformCount = channelPlatformCounts[channelName]?.size || 0;
        platform = platformCount > 1 ? `All ${channel}` : '';
      }

      return {
        name: capitalizeWords(name),
        data: groupData,
        grouping: { channel, platform, tactic, brand_nonbrand }
      };
    })
  )(d);

  let groupedLines = [] as any[];
  if (Array.isArray(transformedData) && transformedData.length > 0 && transformedData[0].grouping) {
    groupedLines = transformedData.reduce((acc: { channel: string; areas: any[] }[], obj) => {
      const { channel } = obj.grouping || { channel: 'unknown' }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  return { newD: transformedData, meanValues, groupedLines };
};

export const decayDataFormatter = (
  data: any[],
  yAxisDataKey: string,
  channelPlatformCounts: Record<string, Set<string>>
): { newD; lines; groupedLines } => {
  const d = R.groupBy(item => R.prop('period', item), data);
  const newD = [] as any[];
  const grouping = [] as any[];

  Object.keys(d).forEach((key, index) => {
    newD[index] = {};
    newD[index].date = key;

    d[key].forEach((item: any) => {
      newD[index][item.name] = item[yAxisDataKey] * 100;
      grouping.push({});

      if (item.platform === null && item.tactic === null && item.brand_nonbrand === null) {
        // Check if this channel has multiple platforms using channelBreakoutMap
        const channelName = item.channel;
        const platformCount = channelPlatformCounts[channelName]?.size || 0;
        const platformLabel = platformCount > 1 ? `All ${capitalizeWords(item.channel)}` : '';

        grouping[index][item.name] = {
          channel: capitalizeWords(item.channel),
          platform: platformLabel,
          tactic: null,
          brand_nonbrand: null
        };
      } else {
        grouping[index][item.name] = {
          channel: capitalizeWords(item.channel),
          platform: capitalizeWords(item.platform),
          tactic: capitalizeWords(item.tactic),
          brand_nonbrand: capitalizeWords(item.brand_nonbrand)
        };
      }
    });
  });

  const keys = Object.keys(newD[0]).filter(key => key !== 'date');
  const lines: {
    dataKey: string;
    grouping?: { channel: string; platform: string; tactic: string; brand: string };
    name: string;
  }[] = [];
  keys.forEach((key, index) => {
    lines[index] = {
      name: capitalizeWords(key),
      dataKey: key,
      grouping: grouping ? grouping[0][key] : null
    };
  });

  let groupedLines = [] as any[];
  if (Array.isArray(lines) && lines.length > 0 && lines[0].grouping) {
    groupedLines = lines.reduce((acc: { channel: string; areas: any[] }[], obj) => {
      const { channel } = obj.grouping || { channel: 'unknown' }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  return { newD, lines, groupedLines };
};

/*
 * GEOGRAPHIC ANALYSIS
 */

// Turns array of objects, where each object is a row in the table,
// into an array of arrays, where each inner array is a row in the table
// each inner array is an array of objects, where each object is a cell in the table
export const formatROITableData = ({
  data,
  tableHeaders,
  outcomeVariable
}: {
  data: any[];
  tableHeaders: any[];
  outcomeVariable: 'revenue' | 'kpiVolume';
}): any[] => {
  const newD = [] as any[];
  const newDimension = [] as any[];

  data.forEach((item: any, i) => {
    newD[i] = [];
    newDimension[i] = [];
    tableHeaders.forEach(header => {
      const label =
        typeof item[header.id] === 'number' || item[header.id] === null
          ? item[header.id]
          : capitalizeWords(item[header.id]);
      let formattedLabel;

      switch (header.id) {
        case 'nextDollarEffectiveness':
          // Always a dollar figure: revenue clients get the "Next Best Dollar Effectiveness"
          // value; kpiVolume clients get the already-reciprocal "Next Dollar Marginal CPA"
          // value (sourced in NextMediaDollar.tsx) - both are dollars, so formatMoney applies
          // unconditionally regardless of outcomeVariable.
          if (item[header.id] === null || item[header.id] === undefined) {
            formattedLabel = '--';
          } else if (item[header.id] === 0) {
            formattedLabel = '$0';
          } else if (item[header.id] < 0.01) {
            formattedLabel = '<$0.01';
          } else if (item[header.id] >= 0.01) {
            formattedLabel = formatMoney(Math.abs(item[header.id]), 2);
          }
          break;
        case 'predictedResponseToMedianSpendLow':
        case 'predictedResponseToMedianSpend':
        case 'predictedResponseToMedianSpendHigh':
        case 'previousQuarterMedianSpend':
          if (item[header.id] === null || item[header.id] === undefined) {
            formattedLabel = '--';
          } else if (outcomeVariable === 'revenue' || header.id === 'previousQuarterMedianSpend') {
            if (item[header.id] === 0) {
              formattedLabel = '$0';
            } else if (item[header.id] < 0.5) {
              formattedLabel = '<$1';
            } else if (item[header.id] < 1) {
              formattedLabel = '$1';
            } else if (item[header.id] > 1) {
              formattedLabel = formatMoney(item[header.id], 0);
            }
          } else if (item[header.id] === 0) {
            formattedLabel = '0';
          } else if (item[header.id] < 0.5) {
            formattedLabel = '<1';
          } else if (item[header.id] < 1) {
            formattedLabel = '1';
          } else if (item[header.id] > 1) {
            formattedLabel = formatNumber(item[header.id], 0);
          }
          break;
        case 'name':
          formattedLabel = capitalizeWords(item[header.id]).replace(/,/g, ' –');
          break;
        default:
          if (item[header.id] === null || item[header.id] === undefined) {
            formattedLabel = '--';
          } else if (item[header.id] === 0) {
            formattedLabel = outcomeVariable === 'revenue' ? '0' : '$0';
          } else if (item[header.id] < 0.01) {
            formattedLabel = outcomeVariable === 'revenue' ? '<0.01' : '<$0.01';
          } else if (item[header.id] >= 0.01) {
            const convDecimal = item[header.id] > 15 ? 0 : 2;
            formattedLabel =
              outcomeVariable === 'revenue'
                ? formatNumber(item[header.id], convDecimal)
                : formatMoney(item[header.id], convDecimal);
          }
          break;
      }

      if (header.id === 'name') {
        const contentLabel = formattedLabel || formattedLabel === '0' ? `${formattedLabel}` : '--';
        newDimension[i].push({
          [header.dimensionTypeName]: {
            value: label,
            label: contentLabel
          }
        });
      }

      // Label is the value of the cell; value: header.label is the column name
      const contentLabel = formattedLabel || formattedLabel === 0 ? formattedLabel : '--';
      newD[i].push({
        value: label,
        label: contentLabel
      });
    });
  });

  if (!R.isEmpty(newDimension[0])) {
    return newDimension.flat();
  }

  return newD;
};
